/**
 * A folder on the reader's own computer, as the detection rules read one (§15).
 *
 * The app points at a repository with an absolute path; this page cannot, and
 * should not be able to. What it has instead is a folder the person **grants**
 * it — a directory picker, or a folder input where there is no picker — and two
 * methods over whatever comes back: `list` and `read`, the `RepoReader` the
 * shared rules in `@airlock/contracts/workspace/detect.ts` take. The rules are
 * then literally the app's, so a project prefilled here and the same folder
 * opened in Airlock describe the same repository.
 *
 * **Nothing is uploaded.** Every byte read here is read by this page, in this
 * browser, and what leaves is only the handful of fields the person then
 * publishes — a remote, a branch, some commands. The values of variables that
 * look like credentials are not even carried that far (`detect-repo.ts`). This
 * is worth stating in the interface as well, and it is: a folder picker is the
 * one control on this page that reaches something outside it.
 *
 * ## Two ways in, because one of them is Chromium-only
 *
 * `showDirectoryPicker` is the right one: a handle, read lazily, so a folder with
 * a 4 GB `node_modules` costs nothing because nothing walks into it. Firefox and
 * Safari have no such thing, so there the fallback is
 * `<input type="file" webkitdirectory>`, which hands over every file's `File`
 * object at once — metadata, not contents, so it is cheap enough — and a reader
 * over that list answers the same two questions.
 *
 * The fallback's one real limitation is dotfiles: a browser that leaves `.git`
 * and `.env` out of a directory input gives a reading with no remote and no
 * environment. That is why the picker is preferred wherever it exists, and why
 * what was found is shown before it is accepted.
 */
import { SKIP, type RepoEntry, type RepoReader } from '@airlock/contracts/workspace/detect-read';

/**
 * What a reader is allowed to hand the rules, per file — the app's own ceiling
 * (`detect-read.ts`). Nothing past the first few kilobytes of a manifest means
 * anything, and a truncated read parses as malformed, which every rule already
 * treats as "the repository does not say".
 */
const MAX_FILE_BYTES = 1_000_000;

/** How deep a picked folder is searched for repositories, as the app searches (§15). */
const MAX_DEPTH = 3;

/** And how many it takes, so pointing at a home directory cannot run away. */
const MAX_REPOS = 40;

/** One repository found under the folder somebody picked. */
export interface FoundRepo {
  /** Its own folder's name — the project's name, before anybody edits it. */
  name: string;
  /**
   * Where it sits under the picked folder, `/`-separated — `services/api`, or
   * the folder's own name when the folder *is* the repository. It is the
   * project's `relPath`: where it lands under the folder each member picks.
   */
  relPath: string;
  reader: RepoReader;
}

/** Whether this browser can be handed a folder at all, and how. */
export function folderPickerKind(): 'picker' | 'input' {
  return typeof window !== 'undefined' && 'showDirectoryPicker' in window ? 'picker' : 'input';
}

/* ── the two readers ───────────────────────────────────────────────────── */

/**
 * A reader over a directory handle. Each call walks down from the root by name
 * rather than keeping handles about, because a repository is read once and a
 * cache of handles is a second thing that can be stale.
 */
function handleReader(root: FileSystemDirectoryHandle): RepoReader {
  const descend = async (parts: string[]): Promise<FileSystemDirectoryHandle | null> => {
    let at = root;
    for (const part of parts) {
      const next = await at.getDirectoryHandle(part).catch(() => null);
      if (!next) return null;
      at = next;
    }
    return at;
  };
  const parts = (path: string): string[] => path.split('/').filter(Boolean);
  return {
    async list(dir: string): Promise<readonly RepoEntry[]> {
      const at = await descend(parts(dir));
      if (!at) return [];
      const out: RepoEntry[] = [];
      try {
        for await (const entry of at.values()) out.push({ name: entry.name, directory: entry.kind === 'directory' });
      } catch {
        // A folder the grant does not cover, or one removed mid-read: the rules
        // take an empty listing as "the repository does not say".
        return out;
      }
      return out;
    },
    async read(path: string): Promise<string | null> {
      const segments = parts(path);
      const name = segments.pop();
      if (!name) return null;
      const at = await descend(segments);
      const handle = at ? await at.getFileHandle(name).catch(() => null) : null;
      if (!handle) return null;
      return await handle
        .getFile()
        .then((file) => file.slice(0, MAX_FILE_BYTES).text())
        .catch(() => null);
    }
  };
}

/**
 * A reader over what a folder input handed over: every file, keyed by the path
 * inside the picked folder. Directories are not in the list at all — they exist
 * exactly when something under them does, which is what a listing says too.
 */
function listReader(files: Map<string, File>): RepoReader {
  const paths = [...files.keys()];
  return {
    list(dir: string): Promise<readonly RepoEntry[]> {
      const prefix = dir ? `${dir}/` : '';
      const seen = new Map<string, boolean>();
      for (const path of paths) {
        if (!path.startsWith(prefix)) continue;
        const rest = path.slice(prefix.length);
        const cut = rest.indexOf('/');
        if (!rest) continue;
        if (cut < 0) seen.set(rest, seen.get(rest) ?? false);
        else seen.set(rest.slice(0, cut), true);
      }
      return Promise.resolve([...seen].map(([name, directory]) => ({ name, directory })));
    },
    read(path: string): Promise<string | null> {
      const file = files.get(path);
      return file ? file.slice(0, MAX_FILE_BYTES).text() : Promise.resolve(null);
    }
  };
}

/** The same reader, rooted one folder down — what one repository of many reads through. */
function under(reader: RepoReader, root: string): RepoReader {
  if (!root) return reader;
  const at = (rel: string): string => (rel ? `${root}/${rel}` : root);
  return { list: (dir) => reader.list(at(dir)), read: (path) => reader.read(at(path)) };
}

/* ── finding the repositories in it ────────────────────────────────────── */

/**
 * A repository is a folder with a `.git` in it — the app confirms with
 * `git rev-parse`, and here the entry existing is as far as it can be taken.
 * A worktree's `.git` is a *file* pointing elsewhere, and that counts too: it is
 * a checkout, it just has no config this page can reach.
 */
async function isRepo(reader: RepoReader, dir: string): Promise<boolean> {
  return (await reader.list(dir)).some((entry) => entry.name === '.git');
}

/**
 * Every repository under the picked folder, the folder itself included — the
 * app's own walk (§15): depth-capped, never descending *into* a repository
 * because one project is one repository, and skipping what `SKIP` names.
 */
async function walk(reader: RepoReader, name: string, dir: string, depth: number, out: FoundRepo[]): Promise<void> {
  if (depth > MAX_DEPTH || out.length >= MAX_REPOS) return;
  if (await isRepo(reader, dir)) {
    out.push({
      name: dir ? (dir.split('/').pop() ?? name) : name,
      // The picked folder being the repository is the ordinary case, and its
      // `relPath` is its own name: a member clones it *into* the folder they
      // pick, not on top of it.
      relPath: dir ? `${name}/${dir}` : name,
      reader: under(reader, dir)
    });
    return;
  }
  for (const entry of await reader.list(dir)) {
    if (!entry.directory || SKIP.has(entry.name) || entry.name.startsWith('.')) continue;
    await walk(reader, name, dir ? `${dir}/${entry.name}` : entry.name, depth + 1, out);
  }
}

export async function reposUnder(reader: RepoReader, name: string): Promise<FoundRepo[]> {
  const out: FoundRepo[] = [];
  await walk(reader, name, '', 0, out);
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

/* ── the two doors ─────────────────────────────────────────────────────── */

/**
 * Ask for a folder. `null` when the person closed the picker, which is not an
 * error and must not be reported as one.
 *
 * `id` and `startIn` make the browser reopen where it opened last time, which is
 * the difference between adding four repositories and navigating to the same
 * parent folder four times.
 */
export async function pickFolder(): Promise<FoundRepo[] | null> {
  const chooser = (window as unknown as { showDirectoryPicker?: (options: unknown) => Promise<FileSystemDirectoryHandle> })
    .showDirectoryPicker;
  if (!chooser) return null;
  let handle: FileSystemDirectoryHandle;
  try {
    handle = await chooser({ id: 'airlock-repo', mode: 'read', startIn: 'documents' });
  } catch (failure) {
    // Closing the picker is the ordinary way out of it and must not be reported
    // as a failure; anything else — a folder the browser refuses to hand over,
    // a page that is not a secure context — is one the person has to be told.
    if (failure instanceof DOMException && failure.name === 'AbortError') return null;
    throw failure;
  }
  return await reposUnder(handleReader(handle), handle.name);
}

/**
 * The same from a folder input's files. `webkitRelativePath` is
 * `<folder>/<path inside it>`, so the first segment is the folder's own name and
 * the rest is the key the reader is asked for.
 */
export async function folderFromFiles(files: readonly File[]): Promise<FoundRepo[]> {
  const inside = new Map<string, File>();
  let name = '';
  for (const file of files) {
    const path = (file as File & { webkitRelativePath?: string }).webkitRelativePath ?? file.name;
    const cut = path.indexOf('/');
    if (cut < 0) continue;
    if (!name) name = path.slice(0, cut);
    inside.set(path.slice(cut + 1), file);
  }
  return name ? await reposUnder(listReader(inside), name) : [];
}
